// Field configuration for the Sohoz Job customer form.
// `name` doubles as the i18n key under `sohozJob.f.*`. Only `applicant_name_bn`,
// `applicant_name_en` and `mobile_number` are required — everything else optional.
//
// Dropdown VALUES are the government form's option values/IDs (e.g. religion "1",
// board "14", university "168", duration "03"). This lets the auto-fill engine set
// `select.value = stored` directly. Each option carries a display label via either:
//   { value, label }  → raw display text (catalogs: boards, universities, …)
//   { value, key }    → i18n key resolved with t(`sohozJob.opts.${key}`)
//
// `v`      = validation profile (see customerValidation.js)
// `max`    = max length (also enforced by sanitization on save)
// `reveal` = conditional child field shown only when the parent select matches.
//            `{ values: [...] }` → show when value is one of these.
//            `{ exclude: [...] }` → show when a value is set and NOT one of these.
//            `extra: [...]` → further child fields revealed (and cleared) together with `field`.

import eduCatalog from '../../../data/bd-education.json';
import districts from '../../../data/bd-districts.json';
import upazilas from '../../../data/bd-upazilas.json';
import epassportNationalities from '../../../data/epassport-nationalities.json';

// Diploma is its own level here, but the gov catalog (scraped, see scripts/build_edu_catalogs.py) lists the
// diploma exams under HSC — the Teletalk form's "HSC / Diploma / equivalent" row. So the diploma lists are
// derived from HSC's at runtime, keeping the gov exam IDs (4 Diploma-in-Engineering, 7 Medical Technology,
// 10 Pharmacy, 11 Arts, 12 Diploma) that the extension's site fillers already map, and HSC loses them.
export const DIPLOMA_EXAM_IDS = new Set(['4', '7', '10', '11', '12']);
// BTEB, State Medical Faculty, Pharmacy Council, Open University, Other.
const DIPLOMA_BOARD_IDS = new Set(['26', '25', '24', '21', '99']);
const DIPLOMA_DURATION_IDS = new Set(['03', '04']);
const edu = {
    ...eduCatalog,
    exams: {
        ...eduCatalog.exams,
        hsc: eduCatalog.exams.hsc.filter((x) => !DIPLOMA_EXAM_IDS.has(x.id)),
        diploma: eduCatalog.exams.hsc.filter((x) => DIPLOMA_EXAM_IDS.has(x.id)),
    },
    boards: { ...eduCatalog.boards, diploma: eduCatalog.boards.hsc.filter((x) => DIPLOMA_BOARD_IDS.has(x.id)) },
    results: { ...eduCatalog.results, diploma: eduCatalog.results.hsc },
    durations: { ...eduCatalog.durations, diploma: eduCatalog.durations.graduation.filter((x) => DIPLOMA_DURATION_IDS.has(x.id)) },
};

// Catalog → option helpers.
const idName = (arr) => (arr || []).map((x) => ({ value: x.id, label: x.name }));
const idKey = (arr) => (arr || []).map((x) => ({ value: x.id, key: x.key }));

// bdjobs.com "Create Account" skill categories: the value is bdjobs' own option id (the same on its
// white-collar, blue-collar and disabled-persons forms; blue-collar ids are 61-92, "Other special" is -11).
// Labels are i18n keys `sohozJob.opts.bdjobs_skill_<id>` (`m11` stands for -11), English and Bangla.
export const BDJOBS_SKILLS = [
    { value: "1", key: "bdjobs_skill_1" },
    { value: "2", key: "bdjobs_skill_2" },
    { value: "3", key: "bdjobs_skill_3" },
    { value: "4", key: "bdjobs_skill_4" },
    { value: "5", key: "bdjobs_skill_5" },
    { value: "6", key: "bdjobs_skill_6" },
    { value: "7", key: "bdjobs_skill_7" },
    { value: "8", key: "bdjobs_skill_8" },
    { value: "9", key: "bdjobs_skill_9" },
    { value: "10", key: "bdjobs_skill_10" },
    { value: "11", key: "bdjobs_skill_11" },
    { value: "12", key: "bdjobs_skill_12" },
    { value: "13", key: "bdjobs_skill_13" },
    { value: "14", key: "bdjobs_skill_14" },
    { value: "15", key: "bdjobs_skill_15" },
    { value: "16", key: "bdjobs_skill_16" },
    { value: "17", key: "bdjobs_skill_17" },
    { value: "18", key: "bdjobs_skill_18" },
    { value: "19", key: "bdjobs_skill_19" },
    { value: "20", key: "bdjobs_skill_20" },
    { value: "22", key: "bdjobs_skill_22" },
    { value: "24", key: "bdjobs_skill_24" },
    { value: "26", key: "bdjobs_skill_26" },
    { value: "27", key: "bdjobs_skill_27" },
    { value: "28", key: "bdjobs_skill_28" },
    { value: "29", key: "bdjobs_skill_29" },
    { value: "30", key: "bdjobs_skill_30" },
    { value: "61", key: "bdjobs_skill_61" },
    { value: "62", key: "bdjobs_skill_62" },
    { value: "63", key: "bdjobs_skill_63" },
    { value: "64", key: "bdjobs_skill_64" },
    { value: "65", key: "bdjobs_skill_65" },
    { value: "66", key: "bdjobs_skill_66" },
    { value: "67", key: "bdjobs_skill_67" },
    { value: "68", key: "bdjobs_skill_68" },
    { value: "69", key: "bdjobs_skill_69" },
    { value: "70", key: "bdjobs_skill_70" },
    { value: "71", key: "bdjobs_skill_71" },
    { value: "72", key: "bdjobs_skill_72" },
    { value: "73", key: "bdjobs_skill_73" },
    { value: "74", key: "bdjobs_skill_74" },
    { value: "75", key: "bdjobs_skill_75" },
    { value: "76", key: "bdjobs_skill_76" },
    { value: "77", key: "bdjobs_skill_77" },
    { value: "78", key: "bdjobs_skill_78" },
    { value: "79", key: "bdjobs_skill_79" },
    { value: "80", key: "bdjobs_skill_80" },
    { value: "81", key: "bdjobs_skill_81" },
    { value: "82", key: "bdjobs_skill_82" },
    { value: "83", key: "bdjobs_skill_83" },
    { value: "84", key: "bdjobs_skill_84" },
    { value: "85", key: "bdjobs_skill_85" },
    { value: "86", key: "bdjobs_skill_86" },
    { value: "87", key: "bdjobs_skill_87" },
    { value: "88", key: "bdjobs_skill_88" },
    { value: "89", key: "bdjobs_skill_89" },
    { value: "90", key: "bdjobs_skill_90" },
    { value: "91", key: "bdjobs_skill_91" },
    { value: "92", key: "bdjobs_skill_92" },
    { value: "-11", key: "bdjobs_skill_m11" },
];

// Passing-year options (recent first). Gov form's year value === the year itself.
export const YEAR_OPTIONS = Array.from({ length: 2026 - 1979 + 1 }, (_, i) => String(2026 - i));

// Religion: the gov form's option ids (shared by the applicant and the spouse).
const RELIGION_OPTIONS = [{ value: '1', key: 'islam' }, { value: '2', key: 'hinduism' }, { value: '3', key: 'buddhism' }, { value: '4', key: 'christianity' }, { value: '5', key: 'other' }];

// Nationality (applicant and spouse): the e-Passport portal's own list (ordinary capitalisation, Bangladeshi
// first), so the extension picks the exact option there. Each carries its country `code`: in Bangla the label is
// that country's Bangla name plus the portal's word ("ভারত (Indian)"). "Other" stays for older records.
const NATIONALITY_OPTIONS = [
    ...epassportNationalities.map(({ value, code }) => (value === 'Bangladeshi' ? { value, key: 'bangladeshi' } : { value, label: value, code })),
    { value: 'Other', key: 'other' },
];

// Professions. The first seven (and "Other") are BMET's dropdown wording, which the extension matches there;
// the rest is the e-Passport portal's own profession list (same words, ordinary capitalisation), so the
// extension can pick the exact option there. Unknown to BMET, those fall back to its "Other".
const BMET_PROFESSIONS = [{ value: 'Private Service', key: 'prof_private_service' }, { value: 'Business', key: 'prof_business' }, { value: 'Doctor', key: 'prof_doctor' }, { value: 'Engineer', key: 'prof_engineer' }, { value: 'Govt. Service', key: 'prof_govt_service' }, { value: 'Student', key: 'prof_student' }, { value: 'Unemployed', key: 'prof_unemployed' }];
const EPASSPORT_PROFESSIONS = [
    { value: "Accountant", key: 'prof_accountant' },
    { value: "Adviser", key: 'prof_adviser' },
    { value: "Architect", key: 'prof_architect' },
    { value: "Artist", key: 'prof_artist' },
    { value: "Autonomous", key: 'prof_autonomous' },
    { value: "Bachelor", key: 'prof_bachelor' },
    { value: "Banker", key: 'prof_banker' },
    { value: "Barber", key: 'prof_barber' },
    { value: "Barrister at Law", key: 'prof_barrister_at_law' },
    { value: "Blacksmith", key: 'prof_blacksmith' },
    { value: "Boatman", key: 'prof_boatman' },
    { value: "Chartered Accountant", key: 'prof_chartered_accountant' },
    { value: "Chartered Secretary", key: 'prof_chartered_secretary' },
    { value: "Chief Adviser", key: 'prof_chief_adviser' },
    { value: "Chief Justice", key: 'prof_chief_justice' },
    { value: "Cleaner", key: 'prof_cleaner' },
    { value: "Contractor", key: 'prof_contractor' },
    { value: "Cook", key: 'prof_cook' },
    { value: "Cost and Management Accountant (CMA)", key: 'prof_cost_and_management_accountant_cma' },
    { value: "Dependent of Non-Diplomatic Official / Staff in Foreign Mission", key: 'prof_dependent_of_non_diplomatic_official_staff_in_fo' },
    { value: "Dependent on Diplomat", key: 'prof_dependent_on_diplomat' },
    { value: "Driver", key: 'prof_driver' },
    { value: "Farmer", key: 'prof_farmer' },
    { value: "Fisherman", key: 'prof_fisherman' },
    { value: "Guard", key: 'prof_guard' },
    { value: "Journalist", key: 'prof_journalist' },
    { value: "Judge", key: 'prof_judge' },
    { value: "Justice", key: 'prof_justice' },
    { value: "Labor", key: 'prof_labor' },
    { value: "Lawyer", key: 'prof_lawyer' },
    { value: "Mechanic", key: 'prof_mechanic' },
    { value: "Medical Technologist", key: 'prof_medical_technologist' },
    { value: "Member of Local Government", key: 'prof_member_of_local_government' },
    { value: "Member of Parliament", key: 'prof_member_of_parliament' },
    { value: "Merchant Marine Officer", key: 'prof_merchant_marine_officer' },
    { value: "Minister", key: 'prof_minister' },
    { value: "Movie Director", key: 'prof_movie_director' },
    { value: "Nurse", key: 'prof_nurse' },
    { value: "Painter", key: 'prof_painter' },
    { value: "Permanent Officer/ Staff of Autonomous Organization", key: 'prof_permanent_officer_staff_of_autonomous_organizati' },
    { value: "Permanent Officer/ Staff of Nationalised Organisation", key: 'prof_permanent_officer_staff_of_nationalised_organisa' },
    { value: "Pharmacist", key: 'prof_pharmacist' },
    { value: "Pilot", key: 'prof_pilot' },
    { value: "Politician", key: 'prof_politician' },
    { value: "Porter", key: 'prof_porter' },
    { value: "Potter", key: 'prof_potter' },
    { value: "President", key: 'prof_president' },
    { value: "Prime Minister", key: 'prof_prime_minister' },
    { value: "Project Employee", key: 'prof_project_employee' },
    { value: "Retired (Government Service)", key: 'prof_retired_government_service' },
    { value: "Retired (Private Service)", key: 'prof_retired_private_service' },
    { value: "Retired Technocrat", key: 'prof_retired_technocrat' },
    { value: "Salesman", key: 'prof_salesman' },
    { value: "Satuary Body", key: 'prof_satuary_body' },
    { value: "Semi Government Service", key: 'prof_semi_government_service' },
    { value: "Speaker", key: 'prof_speaker' },
    { value: "State Minister", key: 'prof_state_minister' },
    { value: "Sweeper", key: 'prof_sweeper' },
    { value: "Tailor", key: 'prof_tailor' },
    { value: "Teacher", key: 'prof_teacher' },
    { value: "Washerman", key: 'prof_washerman' },
];
const OTHER_PROFESSION = { value: 'Other', key: 'prof_other' };
const HOUSEWIFE = { value: 'Housewife', key: 'prof_housewife' };
// Housewife last before Other (the extension maps it to e-Passport's "House Wife").
const PROFESSION_OPTIONS = [...BMET_PROFESSIONS, ...EPASSPORT_PROFESSIONS, HOUSEWIFE, OTHER_PROFESSION];
// Parents: the same plus deceased and unknown (e-Passport Parental Information offers these).
const PARENT_PROFESSION_OPTIONS = [
    ...BMET_PROFESSIONS, ...EPASSPORT_PROFESSIONS, HOUSEWIFE,
    { value: "Deceased", key: 'prof_deceased' },
    { value: "Unknown", key: 'prof_unknown' },
    OTHER_PROFESSION,
];

export const BASIC_FIELDS = [
    { name: 'mobile_number', type: 'tel', required: true, v: 'phone', max: 25 },
    { name: 'applicant_name_bn', required: true, v: 'name', max: 80 },
    { name: 'applicant_name_en', required: true, v: 'name', max: 80 },
    { name: 'father_name_en', v: 'name', max: 80 },
    { name: 'father_name_bn', v: 'name', max: 80 },
    { name: 'father_phone', type: 'tel', v: 'phone', max: 25 },
    { name: 'father_profession', type: 'combo', options: PARENT_PROFESSION_OPTIONS },
    { name: 'father_nid', v: 'digits', max: 17 },
    { name: 'mother_name_en', v: 'name', max: 80 },
    { name: 'mother_name_bn', v: 'name', max: 80 },
    { name: 'mother_phone', type: 'tel', v: 'phone', max: 25 },
    { name: 'mother_profession', type: 'combo', options: PARENT_PROFESSION_OPTIONS },
    { name: 'mother_nid', v: 'digits', max: 17 },
    { name: 'date_of_birth', type: 'date' },
    { name: 'age', type: 'number', v: 'age' },
    { name: 'gender', type: 'select', options: [{ value: 'Male', key: 'male' }, { value: 'Female', key: 'female' }, { value: 'Third Gender', key: 'third_gender' }] },
    { name: 'religion', type: 'select', options: RELIGION_OPTIONS },
    { name: 'nationality', type: 'combo', options: NATIONALITY_OPTIONS },
    {
        // Divorced / Widower / Widow match the e-Passport list; the spouse fields show wherever that portal asks
        // for a spouse (married, widower, widow) and for the older "Other".
        name: 'marital_status', type: 'select',
        options: [
            { value: 'Single', key: 'single' }, { value: 'Married', key: 'married' }, { value: 'Divorced', key: 'marital_divorced' },
            { value: 'Widower', key: 'marital_widower' }, { value: 'Widow', key: 'marital_widow' }, { value: 'Other', key: 'other' },
        ],
        reveal: {
            panelKey: 'spouseInfo',
            values: ['Married', 'Widower', 'Widow', 'Other'],
            field: { name: 'spouse_name', v: 'name', max: 80 },
            extra: [
                { name: 'spouse_name_bn', v: 'name', max: 80 },
                { name: 'spouse_phone', type: 'tel', v: 'phone', max: 25 },
                { name: 'spouse_profession', type: 'combo', options: PARENT_PROFESSION_OPTIONS },
                { name: 'spouse_nationality', type: 'combo', options: NATIONALITY_OPTIONS },
                { name: 'spouse_religion', type: 'select', options: RELIGION_OPTIONS },
                { name: 'spouse_nid', v: 'digits', max: 17 },
            ],
        },
    },
    { name: 'home_district', type: 'district' },
    // Values are the labels BMET's profession dropdown shows (the extension matches on them).
    { name: 'profession', type: 'combo', options: PROFESSION_OPTIONS },
    { name: 'bdjobs_skill', type: 'select', options: BDJOBS_SKILLS },
    { name: 'email', type: 'email', v: 'email', max: 100 },
    { name: 'national_id', v: 'digits', max: 17 },
    { name: 'birth_registration_no', v: 'digits', max: 17 },
    {
        name: 'passport_id', v: 'alnum', max: 15,
        // Shown only once a passport ID is entered.
        followUps: [
            { name: 'passport_issue_date', type: 'date' },
            { name: 'passport_issue_place', type: 'district' }, // BMET's issue-place list is the districts
            { name: 'passport_expiry_date', type: 'date', toYear: new Date().getFullYear() + 20 }, // expiry lies in the future
        ],
    },
    { name: 'driving_license', v: 'alnum', max: 30 },
];

// "মৌলিক তথ্য" is shown in labelled groups (father / mother / applicant / documents) so related fields read as
// one block. Each lists BASIC_FIELDS names in display order; a field missing here still shows, in a last group.
// The spouse fields are the marital status's reveal and open in their own panel (reveal.panelKey).
export const BASIC_GROUPS = [
    { key: 'fatherInfo', fields: ['father_name_en', 'father_name_bn', 'father_phone', 'father_profession', 'father_nid'] },
    { key: 'motherInfo', fields: ['mother_name_en', 'mother_name_bn', 'mother_phone', 'mother_profession', 'mother_nid'] },
    { key: 'personalInfo', fields: ['date_of_birth', 'age', 'gender', 'religion', 'nationality', 'home_district', 'profession', 'bdjobs_skill', 'email', 'marital_status'] },
    { key: 'idDocuments', fields: ['national_id', 'birth_registration_no', 'passport_id', 'driving_license'] },
];

export const PASSPORT_FOLLOW_UP_NAMES = ['passport_issue_date', 'passport_issue_place', 'passport_expiry_date'];

// Shared base fields, rendered three times with present_/permanent_/emergency_ prefixes.
export const ADDRESS_BASE = [
    { name: 'care_of', v: 'text', max: 100 },
    { name: 'division', type: 'division' },
    { name: 'district', type: 'district' },
    { name: 'upazila', type: 'upazila' },
    // Union parishad / pourashava ward in Bangla (the Ansar & VDP form wants it): the upazila's unions are suggested
    // (UnionPicker), anything else can be typed.
    { name: 'union', v: 'text', max: 80, picker: 'union' },
    // Picked from the district's post office list, which also sets post_office_bn and post_code (PostOfficePicker).
    { name: 'post_office', v: 'text', max: 60, picker: 'postOffice' },
    { name: 'post_office_bn', v: 'text', max: 60, hidden: true },
    { name: 'post_code', v: 'digits', max: 5 },
    { name: 'village_road_house', v: 'text', max: 150 },
    { name: 'village_road_house_bn', v: 'text', max: 150 },
    { name: 'road', v: 'text', max: 100 },
    { name: 'house', v: 'text', max: 100 },
];

// Gov-form style quota & departmental dropdowns with conditional detail inputs.
// Values are the gov option IDs (quota 5 is intentionally absent on the gov form).
export const QUOTA_SELECTS = [
    {
        name: 'quota', type: 'select',
        options: [
            { value: '1', key: 'freedom_fighter_child' }, { value: '2', key: 'martyred_ff_child' },
            { value: '3', key: 'war_heroine_child' }, { value: '4', key: 'physically_challenged' },
            { value: '6', key: 'ethnic_minority' }, { value: '7', key: 'third_gender' },
            { value: '8', key: 'not_applicable' },
        ],
        reveal: { exclude: ['8'], field: { name: 'quota_details', v: 'text', max: 100 } },
    },
    {
        // The gov `dep_status` list is the same on every Teletalk site. The values this
        // once held (RELIP / SCMFP / CPSP / NJLIP / SIPP-2) were SDFBD project names
        // from the form that catalog was first scraped from — they exist nowhere else.
        name: 'departmental_status', type: 'select',
        options: [
            { value: '1', key: 'dept_govt_employee' }, { value: '2', key: 'dept_semi_govt_employee' },
            { value: '3', key: 'dept_autonomous' }, { value: '4', key: 'dept_departmental_candidate' },
            { value: '5', key: 'not_applicable' },
        ],
        reveal: { exclude: ['5'], field: { name: 'staff_id', v: 'alnum', max: 30 } },
    },
];

// Relationship options for the emergency / family "relation" field. These are the exact
// upper-case strings the e-Passport portal offers (/api/v1/common-data/relationships), so a
// stored value maps straight onto that dropdown; the label carries Bangla + English, searchable
// either way. Common relations first, then in-laws, then the rest.
const RELATION_ENUM = [
    ['FATHER', 'পিতা'], ['MOTHER', 'মাতা'], ['SPOUSE', 'স্বামী / স্ত্রী'], ['SON', 'ছেলে'], ['DAUGHTER', 'মেয়ে'],
    ['BROTHER', 'ভাই'], ['SISTER', 'বোন'], ['UNCLE', 'চাচা / মামা'], ['AUNT', 'চাচি / খালা / ফুফু'],
    ['COUSIN', 'চাচাতো / মামাতো ভাই-বোন'], ['NEPHEW', 'ভাতিজা / ভাগনে'], ['NIECE', 'ভাতিজি / ভাগনি'],
    ['GRANDFATHER', 'দাদা / নানা'], ['GRANDMOTHER', 'দাদি / নানি'], ['GRANDSON', 'নাতি'],
    ['FATHER-IN-LAW', 'শ্বশুর'], ['MOTHER-IN-LAW', 'শাশুড়ি'], ['SON-IN-LAW', 'জামাতা'], ['DAUGHTER-IN-LAW', 'পুত্রবধূ'],
    ['BROTHER-IN-LAW', 'দেবর / ভাসুর / শ্যালক / ভগ্নিপতি'], ['SISTER-IN-LAW', 'ননদ / জা / শ্যালিকা / ভাবি'],
    ['FRIEND', 'বন্ধু'], ['COLLEAGUE', 'সহকর্মী'], ['EMPLOYER', 'নিয়োগকর্তা'], ['BUSINESS PARTNER', 'ব্যবসায়িক অংশীদার'],
    ['OTHERS', 'অন্যান্য'],
];
const relationTitleCase = (s) => s.toLowerCase().replace(/\b[a-z]/g, (c) => c.toUpperCase());
export const RELATION_OPTIONS = RELATION_ENUM.map(([value, bn]) => ({ value, label: `${bn} (${relationTitleCase(value)})` }));

// Emergency contact (BOESL "Family Details"). Parents' and spouse's phones live in BASIC_FIELDS
// beside their names.
export const FAMILY_FIELDS = [
    { name: 'emergency_contact_name', v: 'name', max: 80 },
    { name: 'emergency_contact_relation', type: 'combo', options: RELATION_OPTIONS },
    { name: 'emergency_contact_phone', type: 'tel', v: 'phone', max: 25 },
];

// One row of the repeatable "other family members" list (stored in `family_members`).
export const FAMILY_MEMBER_FIELDS = [
    { name: 'name', labelKey: 'family_member_name', v: 'name', max: 80 },
    { name: 'relation', labelKey: 'family_member_relation', type: 'combo', options: RELATION_OPTIONS },
    { name: 'phone', labelKey: 'family_member_phone', type: 'tel', v: 'phone', max: 25 },
];

// One row of the repeatable "languages" list (stored in `languages`). Skill levels as the job portals rate them.
const LANGUAGE_LEVEL_OPTIONS = [{ value: 'high', key: 'skill_high' }, { value: 'medium', key: 'skill_medium' }, { value: 'low', key: 'skill_low' }];
export const LANGUAGE_FIELDS = [
    {
        name: 'language', type: 'combo', v: 'text', max: 40,
        // bdjobs' own language list (its Language Proficiency dropdown offers only these); another language can
        // still be typed in for portals with a free-text box (DPDC).
        options: [
            { value: 'English', key: 'lang_english' }, { value: 'Chinese', key: 'lang_chinese' }, { value: 'Spanish', key: 'lang_spanish' },
            { value: 'French', key: 'lang_french' }, { value: 'German', key: 'lang_german' }, { value: 'Japanese', key: 'lang_japanese' },
        ],
    },
    { name: 'reading', type: 'select', options: LANGUAGE_LEVEL_OPTIONS },
    { name: 'writing', type: 'select', options: LANGUAGE_LEVEL_OPTIONS },
    { name: 'speaking', type: 'select', options: LANGUAGE_LEVEL_OPTIONS },
    { name: 'listening', type: 'select', options: LANGUAGE_LEVEL_OPTIONS },
    { name: 'certification', labelKey: 'language_certification', v: 'text', max: 100 },
];

// One row of the repeatable "references" list (stored in `references`): someone who vouches for the customer.
export const REFERENCE_FIELDS = [
    { name: 'name', labelKey: 'reference_name', v: 'name', max: 120 },
    { name: 'organization', labelKey: 'reference_organization', v: 'text', max: 150 },
    { name: 'designation', labelKey: 'reference_designation', v: 'text', max: 100 },
    { name: 'mobile', labelKey: 'reference_mobile', type: 'tel', v: 'phone', max: 25 },
    { name: 'phone', labelKey: 'reference_phone', type: 'tel', v: 'phone', max: 25 },
    { name: 'email', labelKey: 'reference_email', type: 'email', v: 'email', max: 120 },
    // How the referee knows the customer, as bdjobs lists it (Relative / Family Friend / Academic / Professional / Others);
    // DPDC has no Family Friend and takes it as Others.
    {
        name: 'relation', labelKey: 'reference_relation', type: 'select',
        options: [
            { value: 'relative', key: 'ref_relative' }, { value: 'family_friend', key: 'ref_family_friend' }, { value: 'academic', key: 'ref_academic' },
            { value: 'professional', key: 'ref_professional' }, { value: 'other', key: 'ref_other' },
        ],
    },
];

export const PHYSICAL_FIELDS = [
    { name: 'blood_group', type: 'select', options: ['A+', 'A-', 'B+', 'B-', 'O+', 'O-', 'AB+', 'AB-'], rawOptions: true },
    { name: 'height_feet', type: 'number', v: 'number', max: 4 },
    { name: 'height_inches', type: 'number', v: 'number', max: 4 },
    { name: 'weight_kg', type: 'number', v: 'number', max: 6 },
    { name: 'chest_normal', v: 'text', max: 10 },
    { name: 'chest_expanded', v: 'text', max: 10 },
];

// Education levels driving which academic dropdown options appear (mirrors the
// gov form's fixed JSC / SSC / HSC / Graduation / Masters / M.Phil / Ph.D sections), plus Diploma as a level
// of its own so a customer can keep both an HSC and a diploma (the extension still fills a diploma into the
// gov forms' HSC row when there is no HSC).
export const ACADEMIC_LEVELS = ['jsc', 'ssc', 'hsc', 'diploma', 'graduation', 'masters', 'mphil', 'phd'];

// Fields cleared when the level changes (their valid option set is level-specific).
export const ACADEMIC_LEVEL_DEPENDENT = ['exam_name', 'board_or_university', 'group_subject_degree', 'institute_name', 'educational_institution', 'result_type', 'duration', 'thesis_title'];

// Marks academic rows as written against the CURRENT gov option catalog. The API
// migrates any unstamped row it reads or receives (backend utils/academicLegacy.js) —
// some old IDs meant a different option, e.g. JSC 4 was "Class Eight Equivalent" and is
// now "Junior Scholarship". Stamping is what stops a save from this client being
// mistaken for one of those legacy rows and shifted again.
export const ACADEMIC_SCHEMA_VERSION = 2;
const ACADEMIC_STAMP = '_eduV';

// Stamp every row that carries data. Empty rows are left bare so they still read as
// empty (the stamp alone must never make a blank row look filled).
export function stampAcademicRows(rows) {
    if (!Array.isArray(rows)) return rows;
    return rows.map((row) => (rowHasData(row) ? { ...row, [ACADEMIC_STAMP]: ACADEMIC_SCHEMA_VERSION } : row));
}

// `departmental_status` is a scalar column with nowhere to carry a stamp, so the marker
// rides along in additional_info. Its remap is ambiguous without one: old 5 meant
// "Departmental Candidate – SIPP-2" (now 4), new 5 means "Not Applicable" and must stay.
export function stampAdditionalInfo(info) {
    return { ...(info && typeof info === 'object' ? info : {}), _catalogV: ACADEMIC_SCHEMA_VERSION };
}

// True when an academic row holds anything the user actually entered.
function rowHasData(row) {
    if (!row || typeof row !== 'object') return false;
    return Object.entries(row)
        .some(([k, v]) => k !== ACADEMIC_STAMP && v != null && String(v).trim() !== '');
}

// Sort rank of an academic level (JSC → Ph.D). Rows with no / unknown level sort last.
export function academicLevelRank(level) {
    const i = ACADEMIC_LEVELS.indexOf(level);
    return i === -1 ? ACADEMIC_LEVELS.length : i;
}

// A diploma saved before Diploma was its own level sits under HSC with a diploma exam: move it to the Diploma
// level (when that is still free), so its exam shows in the right list. The backend does the same on save.
export function liftDiplomaRows(rows) {
    if (!Array.isArray(rows) || rows.some((r) => r?.level === 'diploma')) return rows;
    let lifted = false;
    return rows.map((row) => {
        if (lifted || row?.level !== 'hsc' || !DIPLOMA_EXAM_IDS.has(String(row.exam_name ?? ''))) return row;
        lifted = true;
        return { ...row, level: 'diploma' };
    });
}

// Rows ordered JSC → SSC → HSC → Diploma → … → Ph.D (old HSC-diploma rows lifted first). Stable: rows sharing
// a rank (e.g. two blank rows) keep their relative order.
export function sortAcademicRows(rows) {
    rows = liftDiplomaRows(rows);
    if (!Array.isArray(rows) || rows.length < 2) return rows;
    return rows
        .map((row, i) => ({ row, i }))
        .sort((a, b) => academicLevelRank(a.row?.level) - academicLevelRank(b.row?.level) || a.i - b.i)
        .map((x) => x.row);
}

// Levels still selectable for the row at `index` — each level may be used once,
// but the row's own current level always stays in its list.
export function availableAcademicLevels(rows, index) {
    const taken = new Set(
        (Array.isArray(rows) ? rows : [])
            .map((row, i) => (i === index ? null : row?.level))
            .filter(Boolean),
    );
    return ACADEMIC_LEVELS.filter((lvl) => !taken.has(lvl));
}

const HIGHER_LEVELS = new Set(['graduation', 'masters', 'mphil', 'phd']);
const isHigherLevel = (level) => HIGHER_LEVELS.has(level);

// The gov form narrows the group / subject list to one type letter based on the chosen
// exam (main.js onChangeExamTypeSSC / HSC / GRA); 'Z' ("Other") is always included.
// Offering the unfiltered list lets an applicant save a value the gov dropdown will
// never contain — autofill then leaves the field empty.
const SSC_GROUP_TYPE_BY_EXAM = { 3: 'V', 6: 'V' };                  // default 'G'
const HSC_GROUP_TYPE_BY_EXAM = { 4: 'T', 7: 'M', 8: 'V', 10: 'M' }; // default 'G'
const GRA_SUBJECT_TYPE_BY_EXAM = { 1: 'E', 2: 'A', 3: 'M', 4: 'G', 5: 'P', 6: 'F', 7: 'B', 8: 'G' };

// Options of `type`, plus the always-allowed 'Z'. A null type means "no exam picked
// yet" — show everything rather than an empty dropdown.
function ofType(list, type) {
    return type ? list.filter((o) => o.type === type || o.type === 'Z') : list;
}

// Group / subject options for a level + exam. Graduation has its OWN catalog
// (`gradSubjects`): it carries degree names and engineering subjects that duplicate the
// general ones under different IDs (185 = CSE type G, 394 = CSE type E), so reusing the
// masters list here stores an ID the graduation dropdown does not offer.
export function academicGroupOptions(level, examId) {
    const exam = Number(examId) || 0;
    if (level === 'ssc') return ofType(edu.sscGroups, exam ? (SSC_GROUP_TYPE_BY_EXAM[exam] || 'G') : null);
    // Diploma trades (Civil / Computer / Electrical Technology …) are HSC group types T / M.
    if (level === 'hsc' || level === 'diploma') return ofType(edu.hscGroups, exam ? (HSC_GROUP_TYPE_BY_EXAM[exam] || 'G') : null);
    if (level === 'graduation') return ofType(edu.gradSubjects, GRA_SUBJECT_TYPE_BY_EXAM[exam] || null);
    return edu.subjects; // masters / mphil / phd — the gov form filters none of these
}

// Build the academic field list for a given level and the exam picked in that row.
// Options carry gov IDs (scraped from the Teletalk gov form). Group/Subject is a
// searchable dropdown for every level. `combo` = searchable select (~190 entries).
export function academicFieldsFor(level, examId) {
    const isHigher = isHigherLevel(level);

    // JSC stores the school under its own `institute_name` key (gov form: jsc_institute),
    // separate from the SSC/HSC group and graduation/masters subject fields.
    const groupField = level === 'jsc'
        ? { name: 'institute_name', labelKey: 'institute', v: 'text', max: 100 }
        : (level === 'ssc' || level === 'hsc' || level === 'diploma' || isHigher)
            ? { name: 'group_subject_degree', type: 'combo', options: idName(academicGroupOptions(level, examId)) }
            : { name: 'group_subject_degree', v: 'text', max: 100 };

    const durationCatalog = level === 'graduation' || level === 'diploma'
        ? edu.durations[level]
        : (edu.durations[level] || edu.durations.masters);

    return [
        { name: 'exam_name', type: 'select', options: idName(edu.exams[level]) },
        { name: 'roll_number', v: 'alnum', max: 30 },
        {
            name: 'board_or_university', type: isHigher ? 'combo' : 'select',
            labelKey: isHigher ? 'university' : 'board',
            options: isHigher ? idName(edu.universities) : idName(edu.boards[level]),
        },
        groupField,
        // SSC / HSC / Diploma school, college or polytechnic by name (JSC's is institute_name above; at graduation
        // and above the university is the institution): NESCO and other forms ask for it.
        ...(level === 'ssc' || level === 'hsc' || level === 'diploma' ? [{ name: 'educational_institution', labelKey: 'institute', v: 'text', max: 150 }] : []),
        // Result types differ per level on the gov form (SSC has no "Passed",
        // graduation has no 3rd Class, …) — never share one list across levels.
        { name: 'result_type', type: 'select', options: idKey(edu.results[level]) },
        { name: 'result', v: 'text', max: 30 },
        { name: 'passing_year', type: 'select', options: YEAR_OPTIONS, rawOptions: true },
        // JSC too: the board results site asks for its registration number.
        { name: 'registration_number', v: 'alnum', max: 30 },
        // A diploma takes 3–4 years (bdjobs asks for it), like the degrees.
        ...(isHigher || level === 'diploma' ? [{
            name: 'duration', type: 'select',
            options: idName(durationCatalog),
        }] : []),
        ...(level === 'phd' ? [{ name: 'thesis_title', v: 'text', max: 200 }] : []),
        // When the result was published (Controller of Examinations / Registrar) — Bangladesh Bank / BSCS
        // forms ask for it. Every level has one.
        { name: 'result_published_date', type: 'date' },
    ];
}

// Fields shown before a level is chosen — only the level selector is meaningful;
// the rest stay free text so an entry is never blocked on picking a level.
export const ACADEMIC_FALLBACK = [
    { name: 'exam_name', v: 'text', max: 60 },
    { name: 'roll_number', v: 'alnum', max: 30 },
    { name: 'board_or_university', v: 'text', max: 100 },
    { name: 'group_subject_degree', v: 'text', max: 100 },
    { name: 'result_type', v: 'text', max: 30 },
    { name: 'result', v: 'text', max: 30 },
    { name: 'passing_year', type: 'select', options: YEAR_OPTIONS, rawOptions: true },
    { name: 'registration_number', v: 'alnum', max: 30 },
];

export const EXPERIENCE_FIELDS = [
    {
        name: 'employment_type', type: 'select',
        options: [
            { value: '1', key: 'regular_revenue' }, { value: '2', key: 'adhoc_revenue' }, { value: '3', key: 'temporary_revenue' },
            { value: '4', key: 'work_charged_revenue' }, { value: '5', key: 'temporary_development' }, { value: '6', key: 'work_charged_development' },
            { value: '7', key: 'autonomous' }, { value: '8', key: 'private' }, { value: '9', key: 'self_employed' },
        ],
    },
    { name: 'organization_name', v: 'text', max: 120 },
    { name: 'designation', v: 'text', max: 80 },
    { name: 'office_address', v: 'text', max: 120 },
    { name: 'start_date', type: 'date' },
    { name: 'end_date', type: 'date' },
    { name: 'duration', v: 'text', max: 30 },
    { name: 'last_salary', type: 'number', v: 'number', max: 10 },
    { name: 'currently_working', type: 'checkbox' },
    { name: 'job_description', type: 'textarea', colSpan: 'full', v: 'text', max: 1000 },
];

// ── AI import → gov-value resolution ──────────────────────────────────────────
// The CV parser returns readable slugs (e.g. "islam") for enums and free text for
// academic. Map those onto the stored gov values/IDs so the auto-fill stays valid.

const norm = (s) => String(s ?? '').toLowerCase().replace(/[^a-z0-9]/g, '');

// Find a catalog id by best-effort name match (exact-normalized, then contains).
// Catalogs are built via idName() → { value, label }; older callers may pass { name }.
// Reading the wrong key yields an empty string whose `n.includes('')` is always true,
// which silently matches the FIRST entry of every catalog — so support both keys and
// guard against empty labels.
function findId(catalog, text) {
    const n = norm(text);
    if (!n) return '';
    const nameOf = (o) => norm(o.label ?? o.name ?? '');
    let hit = catalog.find((o) => nameOf(o) === n);
    if (!hit) hit = catalog.find((o) => { const c = nameOf(o); return c && (c.includes(n) || n.includes(c)); });
    return hit ? hit.value : '';
}

const toAsciiDigits = (s) => String(s ?? '').replace(/[০-৯]/g, (d) => String(d.charCodeAt(0) - 0x09E6));

// Course length ("মেয়াদ"). Catalog ids are zero-padded ("03", "04", "1.5"), so a CV's
// "4 Years" / "৪ বছর" never matches by name — compare the numbers instead.
function findDurationId(catalog, text) {
    const byName = findId(catalog, text);
    if (byName) return byName;
    const num = (toAsciiDigits(text).match(/\d+(?:\.\d+)?/) || [])[0];
    if (!num) return '';
    const hit = catalog.find((o) => parseFloat(o.value) === parseFloat(num));
    return hit ? hit.value : '';
}

// AI enum slug → stored gov value, keyed by field then slug.
// Bangla labels are listed alongside the slugs because the CV itself is often a
// Bangla bio-data (this app's own PDF included) and the model echoes what it read.
// norm() strips Bangla to '', so Bangla keys are matched on the raw string.
const ENUM_TO_VALUE = {
    // The parser's gender vocabulary is male/female/other, while the gov form's third
    // option is "Third Gender" — without the `other` key an extracted third-gender
    // applicant silently loses the field.
    gender: {
        male: 'Male', female: 'Female', third_gender: 'Third Gender', other: 'Third Gender',
        'পুরুষ': 'Male', 'মহিলা': 'Female', 'নারী': 'Female', 'তৃতীয় লিঙ্গ': 'Third Gender',
    },
    religion: {
        islam: '1', hinduism: '2', buddhism: '3', christianity: '4', other: '5',
        'ইসলাম': '1', 'হিন্দু': '2', 'বৌদ্ধ': '3', 'খ্রিস্টান': '4', 'খ্রিষ্টান': '4', 'অন্যান্য': '5',
    },
    marital_status: {
        single: 'Single', married: 'Married', other: 'Other', divorced: 'Other', widowed: 'Other',
        'অবিবাহিত': 'Single', 'বিবাহিত': 'Married', 'তালাকপ্রাপ্ত': 'Other', 'বিধবা': 'Other', 'বিপত্নীক': 'Other',
    },
    // the form only offers "Bangladeshi"; anything else has no option
    nationality: { bangladeshi: 'Bangladeshi', 'বাংলাদেশী': 'Bangladeshi', 'বাংলাদেশি': 'Bangladeshi' },
    quota: {
        freedom_fighter_child: '1', martyred_ff_child: '2', war_heroine_child: '3',
        physically_challenged: '4', ethnic_minority: '6', third_gender: '7', not_applicable: '8',
        'মুক্তিযোদ্ধার সন্তান': '1', 'শহীদ মুক্তিযোদ্ধার সন্তান': '2', 'বীরাঙ্গনার সন্তান': '3',
        'শারীরিক প্রতিবন্ধী': '4', 'ক্ষুদ্র নৃগোষ্ঠী': '6', 'তৃতীয় লিঙ্গ': '7', 'প্রযোজ্য নয়': '8',
    },
    departmental_status: {
        dept_govt_employee: '1', dept_semi_govt_employee: '2', dept_autonomous: '3',
        dept_departmental_candidate: '4', not_applicable: '5',
        govt_employee: '1', semi_govt_employee: '2', autonomous: '3', departmental_candidate: '4',
        'সরকারি কর্মচারী': '1', 'আধা-সরকারি কর্মচারী': '2', 'স্বায়ত্তশাসিত': '3',
        'বিভাগীয় প্রার্থী': '4', 'প্রযোজ্য নয়': '5',
    },
};

// experience[].employment_type slug / label → gov option ID.
const EMPLOYMENT_TYPE_TO_VALUE = {
    regular_revenue: '1', adhoc_revenue: '2', temporary_revenue: '3', work_charged_revenue: '4',
    temporary_development: '5', work_charged_development: '6', autonomous: '7', private: '8', self_employed: '9',
    'নিয়মিত ভিত্তিতে (রাজস্ব বাজেট)': '1', 'অ্যাডহক ভিত্তিতে (রাজস্ব বাজেট)': '2',
    'অস্থায়ী ভিত্তিতে (রাজস্ব বাজেট)': '3', 'ওয়ার্ক চার্জড (রাজস্ব বাজেট)': '4',
    'অস্থায়ী (উন্নয়ন প্রকল্প)': '5', 'ওয়ার্ক চার্জড (উন্নয়ন প্রকল্প)': '6',
    'স্বায়ত্তশাসিত/আধা-স্বায়ত্তশাসিত প্রতিষ্ঠান': '7', 'বেসরকারি প্রতিষ্ঠান': '8', 'ব্যবসা/স্ব-নিযুক্ত': '9',
};

// Look a value up by AI slug (normalized) or by the raw label as printed — Bangla
// labels survive only the raw lookup. Returns '' when nothing matches, since a
// select can't display an off-catalog value.
const mapEnum = (map, raw) => map[norm(raw)] || map[String(raw ?? '').trim()] || '';

// Infer the gov result-type id and clean numeric result from free text like
// "GPA 4.63 (Out of 5)", "CGPA 2.57 (Out of 4)", "First Division"/"Second Class".
// result_type ids — secondary: first_division 1, second_division 2, gpa_5 5;
// higher: first_class 1, second_class 2, gpa_4 4, gpa_5 5.
function resolveResult(rawResult, isHigher) {
    const text = String(rawResult ?? '').trim();
    if (!text) return { result_type: '', result: text };
    const low = text.toLowerCase();
    const num = (text.match(/\d+(?:\.\d+)?/) || [])[0] || '';
    if (/gpa|cgpa|grade/.test(low) || num) {
        const outOf = (low.match(/out\s*of\s*(\d+)/) || [])[1];
        // Secondary GPA is always the 5-scale; higher-ed defaults to the 4-scale
        // unless the text explicitly says out of 5.
        const type = isHigher ? (outOf === '5' ? '5' : '4') : '5';
        return { result_type: type, result: num || text };
    }
    if (/first\s*(?:division|class)/.test(low)) return { result_type: '1', result: text };
    if (/second\s*(?:division|class)/.test(low)) return { result_type: '2', result: text };
    return { result_type: '', result: text };
}

const hasBanglaText = (s) => /[ঀ-৿]/.test(String(s ?? ''));

// The mainstream exam of each level, used when the CV names the exam in Bangla
// ("এসএসসি") — those catalogs list English names only, so no match is possible.
const DEFAULT_EXAM_ID = { jsc: '1', ssc: '1', hsc: '1', diploma: '4', graduation: '4', masters: '10', mphil: '4', phd: '4' };

function resolveAcademicRow(row) {
    if (!row || typeof row !== 'object') return row;
    // Detect the gov level: trust an explicitly extracted level ("শিক্ষাগত স্তর") first,
    // then fall back to matching the exam-name text against each level's catalog.
    let level = ACADEMIC_LEVELS.includes(norm(row.level)) ? norm(row.level) : '';
    if (!level) {
        for (const lvl of ACADEMIC_LEVELS) {
            if (findId(idName(edu.exams[lvl]), row.exam_name)) { level = lvl; break; }
        }
    }
    if (!level) return { ...row }; // unknown level → keep free text; fallback fields show it

    const isHigher = isHigherLevel(level);
    const hasDuration = isHigher || level === 'diploma';
    // Bangla exam catalogs don't exist (names are English), and the model sometimes
    // echoes the Bangla level label under exam_name — fall back to that level's
    // mainstream exam so the row still resolves.
    const examId = findId(idName(edu.exams[level]), row.exam_name)
        || (hasBanglaText(row.exam_name) ? DEFAULT_EXAM_ID[level] : '');
    const boardId = row.board_or_university
        ? (isHigher ? findId(idName(edu.universities), row.board_or_university)
                    : findId(idName(edu.boards[level]), row.board_or_university))
        : '';

    // Only "upgrade" a row to the gov-ID dropdowns when we can map the institution it
    // lists. Otherwise keep the whole row as free text — a select can't display an
    // unmatched value, so converting would silently hide what the CV actually said
    // (e.g. a foreign university not in the BD catalog). An unmatched exam alone no
    // longer discards the row: the level is known, so the rest still maps.
    if (row.board_or_university && !boardId) return { ...row };

    const out = { ...row, level, exam_name: examId, board_or_university: boardId };
    if (level === 'jsc') {
        // JSC keeps a free-text school under institute_name (no group/subject catalog).
        if (row.group_subject_degree && !out.institute_name) out.institute_name = row.group_subject_degree;
        delete out.group_subject_degree;
    } else if (row.group_subject_degree) {
        // Match against the same exam-filtered catalog the form offers, so an imported
        // subject resolves to an ID that level+exam actually accepts.
        const cat = academicGroupOptions(level, examId);
        if (cat) out.group_subject_degree = findId(idName(cat), row.group_subject_degree) || row.group_subject_degree;
    }
    if (hasDuration && row.duration) {
        const durationCatalog = level === 'graduation' || level === 'diploma'
            ? edu.durations[level]
            : (edu.durations[level] || edu.durations.masters);
        out.duration = findDurationId(idName(durationCatalog), row.duration) || '';
    } else if (!hasDuration) {
        delete out.duration; // SSC/HSC have no duration on the gov form
    }
    if (level !== 'phd') delete out.thesis_title;
    // Derive the grade dropdown + clean numeric result (the CV lists them as one string).
    if (out.result && !out.result_type) {
        const r = resolveResult(out.result, hasDuration); // a diploma's CGPA is out of 4, like a degree's
        if (r.result_type) out.result_type = r.result_type;
        out.result = r.result;
    }
    return out;
}

// District fields use the Bangla district name as their stored value, but a CV
// usually spells the district in English. Resolve English (or Bangla) text to the
// stored Bangla name; leave unchanged if there's no match.
const DISTRICT_FIELDS = ['home_district', 'present_district', 'permanent_district', 'emergency_district'];
function resolveDistrict(text) {
    const raw = String(text ?? '').trim();
    if (!raw) return text;
    // Bangla norms to '' — match it raw before falling back to the English catalog.
    const byBn = districts.find((d) => d.bn_name === raw);
    if (byBn) return byBn.bn_name;
    const n = norm(raw);
    if (!n) return text;
    const hit = districts.find((d) => norm(d.name) === n || norm(d.bn_name) === n)
        || districts.find((d) => { const a = norm(d.name); return a.includes(n) || n.includes(a); });
    return hit ? hit.bn_name : text;
}

// Upazila is stored as its Bangla name too, but a CV spells it in English. The
// combobox only offers upazilas of the selected district, so resolve within that
// district (passed as its already-resolved Bangla name). Leave text as-is on no match.
const UPAZILA_PAIRS = [['present_upazila', 'present_district'], ['permanent_upazila', 'permanent_district'], ['emergency_upazila', 'emergency_district']];
function findUpazila(pool, raw) {
    // Bangla norms to '' (so `n.includes('')` would match anything) — try the raw
    // Bangla name first and only then the normalized English forms.
    const byBn = pool.find((u) => u.bn_name === raw);
    if (byBn) return byBn;
    const n = norm(raw);
    if (!n) return null;
    return pool.find((u) => norm(u.name) === n || norm(u.bn_name) === n)
        || pool.find((u) => { const a = norm(u.name); return a && (a.includes(n) || n.includes(a)); })
        || null;
}

// Returns { upazila, district } — the district comes back too because the combobox
// only lists upazilas of the selected district. When the CV's upazila does not
// belong to the district we resolved (a misread district, or none at all), an
// unambiguous nationwide match repairs the district instead of dropping the upazila.
function resolveUpazila(text, districtBnName) {
    const raw = String(text ?? '').trim();
    if (!raw) return { upazila: text, district: districtBnName };
    const dn = norm(districtBnName);
    const dist = districts.find((d) => d.bn_name === districtBnName)
        || (dn ? districts.find((d) => norm(d.name) === dn || norm(d.bn_name) === dn) : null);

    if (dist) {
        const inDistrict = findUpazila(upazilas.filter((u) => u.district_id === dist.id), raw);
        if (inDistrict) return { upazila: inDistrict.bn_name, district: districtBnName };
    }
    const matches = upazilas.filter((u) => u.bn_name === raw || norm(u.name) === norm(raw));
    if (matches.length === 1) {
        const owner = districts.find((d) => d.id === matches[0].district_id);
        return { upazila: matches[0].bn_name, district: owner ? owner.bn_name : districtBnName };
    }
    return { upazila: raw, district: districtBnName };
}

// Experience rows: only employment_type is a gov-ID dropdown; the rest is free text.
function resolveExperienceRow(row) {
    if (!row || typeof row !== 'object') return row;
    const out = { ...row };
    if (out.employment_type) out.employment_type = mapEnum(EMPLOYMENT_TYPE_TO_VALUE, out.employment_type);
    if (out.currently_working != null) out.currently_working = out.currently_working === true || /^(true|yes|হ্যাঁ|হ্যা)$/i.test(String(out.currently_working).trim());
    return out;
}

// Map an AI-extracted field set onto stored gov values (returns a new object).
export function resolveImportedFields(fields) {
    if (!fields || typeof fields !== 'object') return fields;
    const out = { ...fields };
    for (const k of Object.keys(ENUM_TO_VALUE)) {
        if (out[k]) out[k] = mapEnum(ENUM_TO_VALUE[k], out[k]);
    }
    for (const k of DISTRICT_FIELDS) if (out[k]) out[k] = resolveDistrict(out[k]);
    // Resolve upazilas after districts so the (now-Bangla) district scopes the lookup;
    // a confident upazila match can correct a district the CV got wrong.
    for (const [uKey, dKey] of UPAZILA_PAIRS) {
        if (!out[uKey]) continue;
        const { upazila, district } = resolveUpazila(out[uKey], out[dKey]);
        out[uKey] = upazila;
        if (district) out[dKey] = district;
    }
    if (Array.isArray(out.academic)) out.academic = out.academic.map(resolveAcademicRow);
    if (Array.isArray(out.experience)) out.experience = out.experience.map(resolveExperienceRow);
    return out;
}

// "ভবিষ্যৎ ক্যারিয়ার তথ্য" — what kind of job the applicant wants. Stored as the `career` object
// (one key per field below) and mirrors bdjobs' Career & Application, Preferred Area and Relevant
// Information blocks, so the extension can fill those. Limits follow bdjobs (and the API schema).
export const CAREER_APPLICATION_FIELDS = [
    { name: 'objective', labelKey: 'career_objective', type: 'textarea', max: 1500, colSpan: 'full' },
    { name: 'present_salary', labelKey: 'career_present_salary', type: 'number', v: 'digits', max: 12 },
    { name: 'expected_salary', labelKey: 'career_expected_salary', type: 'number', v: 'digits', max: 12 },
    {
        name: 'job_level', labelKey: 'career_job_level', type: 'select',
        options: [{ value: 'entry', key: 'job_level_entry' }, { value: 'mid', key: 'job_level_mid' }, { value: 'top', key: 'job_level_top' }],
    },
    {
        name: 'job_nature', labelKey: 'career_job_nature', type: 'select',
        options: [
            { value: 'full_time', key: 'job_nature_full_time' }, { value: 'part_time', key: 'job_nature_part_time' },
            { value: 'contract', key: 'job_nature_contract' }, { value: 'internship', key: 'job_nature_internship' },
            { value: 'freelance', key: 'job_nature_freelance' },
        ],
    },
];

export const CAREER_RELEVANT_FIELDS = [
    { name: 'career_summary', labelKey: 'career_summary', type: 'textarea', max: 2000, colSpan: 'full' },
    { name: 'special_qualification', labelKey: 'career_special_qualification', type: 'textarea', max: 1500, colSpan: 'full' },
    { name: 'keywords', labelKey: 'career_keywords', type: 'textarea', max: 500, colSpan: 'full' },
];

// bdjobs splits its job categories into "Functional" (ids below 61) and "Special Skills" (61+, -11).
const isSpecialSkill = (o) => Number(o.value) >= 61 || o.value === '-11';
export const CAREER_FUNCTIONAL_CATEGORIES = BDJOBS_SKILLS.filter((o) => !isSpecialSkill(o));
export const CAREER_SPECIAL_SKILLS = BDJOBS_SKILLS.filter(isSpecialSkill);

// Preferred districts hold Bangla district names (like every other district field); bdjobs also offers this.
export const CAREER_ANYWHERE_IN_BD = 'Anywhere in Bangladesh';

// Max picks per list (bdjobs' limits).
export const CAREER_LIMITS = {
    functional_categories: 3,
    special_skills: 3,
    preferred_districts: 15,
    preferred_countries: 10,
    organization_types: 12,
};

// "প্রতিবন্ধী ব্যক্তির তথ্য (যদি থাকে)": shown only when its section toggle is on (cleared on save when off).
// The ID is the `disability_id` column (also bdjobs' disabled-persons sign-up); the rest is the `disability`
// object, mirroring bdjobs' Disability Information block. Levels are stored as keys; the extension maps
// them to bdjobs' option text.
const DIFFICULTY_OPTIONS = [
    { value: 'some', key: 'difficulty_some' },
    { value: 'a_lot', key: 'difficulty_a_lot' },
    { value: 'cannot', key: 'difficulty_cannot' },
];
// "মুক্তিযোদ্ধার তথ্য": the Freedom Fighter block some job forms ask for (Bangladesh Bank / BSCS e-recruitment).
// Shown under the quota for the freedom-fighter quotas, typed in English as those forms require, and stored as
// the `freedom_fighter` object.
export const FREEDOM_FIGHTER_QUOTAS = ['1', '2', '3'];
export const FREEDOM_FIGHTER_FIELDS = [
    {
        name: 'relation', labelKey: 'ff_relation', type: 'select',
        options: [{ value: 'child', key: 'ff_child' }, { value: 'grandchild', key: 'ff_grandchild' }],
    },
    { name: 'name', labelKey: 'ff_name', v: 'name', max: 80 },
    { name: 'father_name', labelKey: 'ff_father_name', v: 'name', max: 80 },
    { name: 'upazila_district', labelKey: 'ff_upazila_district', v: 'text', max: 120 },
    { name: 'dob', labelKey: 'ff_dob', type: 'date', fromYear: 1920 },
    { name: 'nid', labelKey: 'ff_nid', v: 'digits', max: 17 },
    { name: 'muktibarta', labelKey: 'ff_muktibarta', v: 'text', max: 200 },
    { name: 'certificate', labelKey: 'ff_certificate', v: 'text', max: 200 },
    { name: 'comments', labelKey: 'ff_comments', v: 'text', max: 300 },
];

export const DISABILITY_ID_FIELD = { name: 'disability_id', v: 'digits', max: 30 };
export const DISABILITY_FIELDS = [
    { name: 'show_on_profile', labelKey: 'disability_show_on_profile', type: 'checkbox' },
    ...['see', 'hear', 'concentrate', 'mobility', 'communicate', 'self_care'].map((name) => ({
        name, labelKey: `difficulty_${name}`, type: 'select', options: DIFFICULTY_OPTIONS,
    })),
];
